import { toast } from 'react-toastify'
import rp from 'request-promise-native'
import { uri, uriV2 } from '../defaultProps'
import { customLoginErrorMessage, customResetPasswordErrorMessage, customSendResetPasswordEmailErrorMessage, generalErrorMessage, customPostNotificationPushRegisterErrorMessage } from './customErrorMessages'

const wrappedRequest = async (params, customErrorMessage) => {
  try {
    return await rp(params)
  } catch (err) {
    let errorMessage

    if (err.error && err.error.data && err.error.data.displayMessage) {
      errorMessage = err.error.data.displayMessage
    } else if (err.error.error && err.error.error.data && err.error.error.data.displayMessage) {
      errorMessage = err.error.error.data.displayMessage
    } else if (customErrorMessage) {
      errorMessage = customErrorMessage(err)
    } else {
      errorMessage = generalErrorMessage(err)
    }

    const error = {
      errorMessage,
      statusCode: err.statusCode
    }

    toast.error(errorMessage)
    throw error
  }
}

const localAuth = (email, password) => wrappedRequest({
  uri: `${uriV2}/auth/local`,
  method: 'POST',
  body: {
    email,
    password
  },
  json: true,
  resolveWithFullResponse: true
}, customLoginErrorMessage)

const getUser = token => wrappedRequest({
  uri: `${uri}/user`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getProfile = (token, id) => wrappedRequest({
  uri: `${uri}/media/profile?id=${id}`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getProfileImage = (token, id) => wrappedRequest({
  uri: `${uriV2}/user/media/profile/image?id=${id}`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getStyleImage = token => wrappedRequest({
  uri: `${uriV2}/user/media/style`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getPersonalColor = (token, id) => wrappedRequest({
  uri: `${uri}/media/personalcoloring/image?id=${id}`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getPersonalColoringV2 = (token) => wrappedRequest({
  uri: `${uriV2}/media/personalcoloring/image/all`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getCloset = (token, id) => wrappedRequest({
  uri: `${uri}/media/closet?id=${id}`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getMoodboard = (token, id) => wrappedRequest({
  uri: `${uri}/media/moodboard?id=${id}`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getMoodboardTemplate = token => wrappedRequest({
  uri: `${uriV2}/user/moodboard/template/all`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getShopList = token => wrappedRequest({
  uri: `${uriV2}/user/media/shoplist`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getShopListTemplate = token => wrappedRequest({
  uri: `${uriV2}/user/media/shoplist/template`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getPersonalColoring = token => wrappedRequest({
  uri: `${uri}/media/personalcoloring`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getAgenda = (token) => wrappedRequest({
  uri: `${uriV2}/agenda/events/next`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getPersonalContrast = token => wrappedRequest({
  uri: `${uriV2}/user/media/contrast/image/all`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getOpenCart = token => wrappedRequest({
  uri: `${uriV2}/shop/cart`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getPayerInfo = token => wrappedRequest({
  uri: `${uriV2}/shop/payer`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getDeliveryAddress = token => wrappedRequest({
  uri: `${uriV2}/shop/delivery-address`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getCard = token => wrappedRequest({
  uri: `${uriV2}/payment/customer/card`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getOrders = token => wrappedRequest({
  uri: `${uriV2}/shop/order`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getBlockStatus = token => wrappedRequest({
  uri: `${uriV2}/financial/block/client`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getBillingList = token => wrappedRequest({
  uri: `${uriV2}/financial/billing/client`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getMe = token => wrappedRequest({
  uri: `${uriV2}/user/me`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const resetPassword = (token, password) => wrappedRequest({
  uri: `${uriV2}/auth/reset/submit`,
  method: 'POST',
  headers: {
    Authorization: token
  },
  body: {
    password: password,
    token: token
  },
  json: true
}, customResetPasswordErrorMessage)

const sendResetPasswordEmail = email => wrappedRequest({
  uri: `${uriV2}/auth/reset/request`,
  method: 'POST',
  body: {
    email
  },
  json: true
}, customSendResetPasswordEmailErrorMessage)

// Web push notifications
const getPushNotificationSubscription = (token, userId) => wrappedRequest({
  uri: `${uriV2}/notifications/push/subscription/${userId}`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const getPushNotificationPublicKey = token => wrappedRequest({
  uri: `${uriV2}/notifications/push/public-key`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const postPushNotificationRegister = (token, body) => wrappedRequest({
  uri: `${uriV2}/notifications/push/register/subscription`,
  method: 'POST',
  headers: {
    Authorization: token
  },
  body,
  json: true
}, customPostNotificationPushRegisterErrorMessage)

// Remember You
const postNewRememberYou = (token, body) => wrappedRequest({
  uri: `${uriV2}/remember-you/create`,
  method: 'POST',
  headers: {
    Authorization: token,
    'content-Type': 'image/png'
  },
  body,
  json: true
})

const getRememberYouList = token => wrappedRequest({
  uri: `${uriV2}/remember-you/list`,
  method: 'GET',
  headers: {
    Authorization: token
  },
  json: true
})

const deleteRememberYouById = (token, id) => wrappedRequest({
  uri: `${uriV2}/remember-you/delete/${id}`,
  method: 'DELETE',
  headers: {
    Authorization: token
  }
})

export default {
  localAuth,
  getUser,
  getProfile,
  getProfileImage,
  getStyleImage,
  getPersonalColor,
  getCloset,
  getMoodboard,
  getMoodboardTemplate,
  getShopList,
  getShopListTemplate,
  getPersonalColoring,
  getAgenda,
  getPersonalContrast,
  getOpenCart,
  getPayerInfo,
  getDeliveryAddress,
  getCard,
  getOrders,
  getBlockStatus,
  getBillingList,
  getMe,
  resetPassword,
  sendResetPasswordEmail,
  getPushNotificationSubscription,
  getPushNotificationPublicKey,
  postPushNotificationRegister,
  getPersonalColoringV2,
  postNewRememberYou,
  getRememberYouList,
  deleteRememberYouById
}



// WEBPACK FOOTER //
// ./src/services/Api/index.js